import shaarpLogo from "@/assets/shaarp-logo.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getPromptStyle,
  getPromptTitle,
  getSupportedLang,
  truncateContent,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { useUser } from "@clerk/react";
import { PromptDTO } from "@shaarp/common";
import { LucideIcon } from "lucide-react";
import { memo } from "react";
import PromptActionButtons from "./prompt-action-buttons";

type PromptCardProps = {
  prompt: PromptDTO;
  onSelect: (prompt: PromptDTO) => void;
  onEdit?: (prompt: PromptDTO) => void;
  onRemove?: (prompt: PromptDTO) => void;
  canEdit?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: (prompt: PromptDTO) => void;
  isPinned?: boolean;
  onTogglePinned?: (prompt: PromptDTO) => void;
};

const CARD_BASE_CLASSES = cn(
  "group relative flex items-start gap-4 p-4 cursor-pointer",
  "hover:bg-accent/50",
  "border-border",
  "transition-all duration-200 ease-out",
  "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-background",
);

type CardIconProps = {
  Icon: LucideIcon;
  iconColorClass: string;
  iconBgClass: string;
  scaleClass?: string;
};

function CardIcon({
  Icon,
  iconColorClass,
  iconBgClass,
  scaleClass = "scale-110",
}: CardIconProps) {
  return (
    <div
      className={cn(
        "shrink-0 rounded-full flex items-center justify-center",
        "w-12 h-12",
        `group-hover:${scaleClass} transition-transform duration-200`,
        iconBgClass,
      )}
    >
      <Icon className={cn("w-6 h-6", iconColorClass)} />
    </div>
  );
}

function PromptCard({
  prompt,
  onSelect,
  onEdit,
  onRemove,
  canEdit = false,
  isFavorite = false,
  onToggleFavorite,
  isPinned = false,
  onTogglePinned,
}: PromptCardProps) {
  const { t, i18n } = useTranslation();
  const { user: clerkUser } = useUser();
  const lang = getSupportedLang(i18n.language);
  const handleClick = () => {
    onSelect(prompt);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(prompt);
    }
  };
  const { Icon, colors } = getPromptStyle(prompt);
  const isPublicPrompt = prompt.ownerType === "public";
  const title = getPromptTitle(prompt, lang);
  const content = prompt.translations?.[lang]?.content || prompt.content;

  const updatedAtFormatted = prompt.updatedAt
    ? new Date(prompt.updatedAt).toLocaleDateString(i18n.language)
    : null;
  const lastUpdatedUser = !isPublicPrompt
    ? (prompt.lastUpdatedBy ?? prompt.author ?? null)
    : null;
  const lastUpdatedName = isPublicPrompt
    ? "Shaarp"
    : lastUpdatedUser?.id === clerkUser?.id
      ? t("prompt.card.you")
      : (lastUpdatedUser?.fullName ?? null);
  const updatedTooltip =
    updatedAtFormatted && lastUpdatedName
      ? t("prompt.card.updated", {
          date: updatedAtFormatted,
          author: lastUpdatedName,
        })
      : null;

  // `aria-label` below replaces the card's whole contents for a screen reader,
  // so the automatic-use badge is never announced on its own: what it means is
  // spelled out in the label, or it stays sighted-only.
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`${title} - ${prompt.description || ""}${
        prompt.isAutoEnabled ? ` - ${t("prompt.card.autoUseTooltip")}` : ""
      }`}
      className={CARD_BASE_CLASSES}
    >
      <div className="relative shrink-0">
        <CardIcon
          Icon={Icon}
          iconColorClass={colors.icon}
          iconBgClass={colors.iconBg}
          scaleClass={isPublicPrompt ? "scale-110" : "scale-105"}
        />
        {updatedTooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute -bottom-0.5 -right-0.5 cursor-default ring-2 ring-background rounded-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isPublicPrompt ? (
                    <div className="flex items-center justify-center w-4 h-4 overflow-hidden bg-white rounded-full">
                      <img
                        src={shaarpLogo}
                        alt="Shaarp"
                        className="object-contain w-4 h-4"
                      />
                    </div>
                  ) : (
                    <Avatar className="w-4 h-4">
                      <AvatarImage src={lastUpdatedUser?.imageUrl} />
                      <AvatarFallback className="text-[8px] bg-muted">
                        {(lastUpdatedUser?.firstName?.[0] ?? "") +
                          (lastUpdatedUser?.lastName?.[0] ?? "")}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>{updatedTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2 min-w-0">
          <CardTitle className="text-xs font-semibold truncate">
            {title || t("prompt.card.untitled")}
          </CardTitle>
          {/* Marks what was switched on, not what was not: automatic use is the
              option someone turns on, so the badge is the exception. A library
              where most tactics are manual would otherwise be a wall of badges
              saying nothing happened.

              One word beside a truncated title, with the meaning on hover: the
              badge sits on the line that carries the title, so anything longer
              eats the name of the tactic it labels. The tooltip is what the
              word stands for, and `cursor-default` plus the click guard keep
              reading it from opening the tactic. */}
          {prompt.isAutoEnabled && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="font-normal shrink-0 cursor-default"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("prompt.card.autoUse")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {t("prompt.card.autoUseTooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <CardDescription className="text-sm leading-relaxed line-clamp-2">
          {truncateContent(content)}
        </CardDescription>
      </div>

      <PromptActionButtons
        prompt={prompt}
        canEdit={canEdit}
        isFavorite={isFavorite}
        isPinned={isPinned}
        onEdit={onEdit}
        onRemove={onRemove}
        onToggleFavorite={onToggleFavorite}
        onTogglePinned={onTogglePinned}
      />
    </Card>
  );
}

export default memo(PromptCard);
